import React, { useEffect, useMemo, useRef, useState } from "react";

import TimelineIcon from "@uui/NotificationText";
import { findIndex } from "lodash/fp";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import styled from "@mui/material/styles/styled";

import IconHeading from "@app/analytics/components/IconHeading";
import ReportSubsection from "@app/analytics/components/reports/ReportSubsection";
import TimelineItem from "@app/analytics/TopicReport/components/TimelineItem";
import useTopicReport from "@app/analytics/TopicReport/hooks/useTopicReport";
import useTopicReportEntities from "@app/analytics/TopicReport/hooks/useTopicReportEntities";
import useTopicReportStore from "@app/analytics/TopicReport/hooks/useTopicReportStore";
import { SubSectionContainer } from "@app/analytics/TopicReport/styles";
import { useTopicReportContext } from "@app/analytics/TopicReport/TopicReportContext";
import { groupEntitiesByDate } from "@app/analytics/TopicReport/utils";
import { shouldSmoothScroll } from "@app/analytics/utils";

import { Column, Row } from "@rui/Stack";

const VIRTUOSO_ID = "timeline-virtuoso";

const VirtuosoContainer = styled(Column)({
  paddingLeft: "4px",
  "[data-item-index]:first-of-type": {
    ".timeline-item__connector--top": {
      backgroundColor: "transparent",
    },
  },
  "[data-item-index]:last-of-type": {
    ".timeline-item__connector--bottom": {
      backgroundColor: "transparent",
    },
  },
});

const TopicReportTimeline = () => {
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);

  const { topic } = useTopicReport();
  const { allEntities } = useTopicReportEntities();
  const { mainScrollContainer } = useTopicReportContext();
  const activeTimelineItemId = useTopicReportStore(
    (state) => state.activeTimelineItemId
  );

  const timelineGroups = useMemo(
    () => groupEntitiesByDate(allEntities),
    [allEntities.length]
  );

  useEffect(() => {
    if (activeTimelineItemId == null) return;
    const activeTimelineItemIndex = findIndex(
      { id: activeTimelineItemId },
      timelineGroups
    );

    if (activeTimelineItemIndex > -1) {
      virtuosoRef.current?.scrollToIndex({
        index: activeTimelineItemIndex,
        behavior: shouldSmoothScroll(VIRTUOSO_ID, activeTimelineItemIndex)
          ? "smooth"
          : "auto",
        offset: -90,
      });
    }
  }, [activeTimelineItemId]);

  // to appease the types
  if (!topic) return null;

  const currentActiveTimelineItem = activeTimelineItemId ?? activeItemId;

  return (
    <SubSectionContainer>
      <ReportSubsection
        id="timeline"
        spacing={2}
        disablePaddingTop
        sx={{ px: 0 }}
      >
        <Row paddingY={2}>
          <IconHeading
            startIcon={<TimelineIcon sx={{ color: "shades.primary.500" }} />}
            color="shades.ghost.500"
          >
            Timeline
          </IconHeading>
        </Row>
        <VirtuosoContainer>
          {mainScrollContainer && (
            <Virtuoso
              id={VIRTUOSO_ID}
              ref={virtuosoRef}
              customScrollParent={mainScrollContainer}
              data={timelineGroups}
              itemContent={(index, item) => (
                <TimelineItem
                  onClick={setActiveItemId}
                  active={
                    currentActiveTimelineItem
                      ? item.id === currentActiveTimelineItem
                      : index === 0
                  }
                  {...item}
                  forceExpand={activeTimelineItemId === item.id}
                />
              )}
              overscan={{ main: 10, reverse: 10 }}
            />
          )}
        </VirtuosoContainer>
      </ReportSubsection>
    </SubSectionContainer>
  );
};

export default TopicReportTimeline;
